import React, { type ReactNode } from "react";
import { Description, Field, Label } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import EmeraldSwitch from "../components/EmeraldSwitch";

export type PrivacyReviewSettings = {
  annualPrivacyReviewEnabled: boolean;
  privacyReviewIntervalDays: string | number;
  privacyReviewPopupEnabled: boolean;
  privacyReviewPopupTitle: string;
  privacyReviewPopupMessage: string;
  privacyReviewSnoozeDays: string | number;
  privacyReviewEmailEnabled: boolean;
  privacyReviewReminderIntervalDays: string | number;
  privacyReviewEmailAdditionalMessage: string;
};

type DayField = "privacyReviewIntervalDays" | "privacyReviewSnoozeDays" | "privacyReviewReminderIntervalDays";

// Same limits as the backend validation in SchoolSettingsService.
export const PRIVACY_REVIEW_DAY_LIMITS: Record<DayField, number> = {
  privacyReviewIntervalDays: 1095,
  privacyReviewSnoozeDays: 365,
  privacyReviewReminderIntervalDays: 365,
};

export const invalidPrivacyReviewDayField = (form: PrivacyReviewSettings): DayField | null =>
  (Object.keys(PRIVACY_REVIEW_DAY_LIMITS) as DayField[]).find((name) => {
    const value = Number(form[name]);
    return String(form[name]).trim() === "" || !Number.isInteger(value) || value < 1 || value > PRIVACY_REVIEW_DAY_LIMITS[name];
  }) ?? null;

export const missingPrivacyReviewPopupText = (form: PrivacyReviewSettings) =>
  form.privacyReviewPopupEnabled &&
  (!form.privacyReviewPopupTitle.trim() || !form.privacyReviewPopupMessage.trim());

type Props = {
  form: PrivacyReviewSettings;
  onChange: <K extends keyof PrivacyReviewSettings>(name: K, value: PrivacyReviewSettings[K]) => void;
};

const inputClass =
  "rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:hover:border-slate-600 dark:disabled:bg-slate-900 dark:disabled:text-slate-500";
const labelClass = "text-sm font-semibold text-slate-950 dark:text-white";
const helpClass = "mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300";
const rowClass = "flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-8";

const Card = ({ title, inactive, hint, delay, children }: {
  title: string;
  inactive?: boolean;
  hint?: string;
  delay: number;
  children: ReactNode;
}) => (
  <section
    className="lds-school-enter rounded-[26px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-7"
    style={{ animationDelay: `${delay}ms` }}
  >
    <h2 className="text-lg font-semibold text-slate-950 dark:text-white">{title}</h2>
    {inactive && hint && (
      <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">{hint}</p>
    )}
    <div className={`mt-2 divide-y divide-slate-100 dark:divide-slate-800 ${inactive ? "opacity-60" : ""}`}>
      {children}
    </div>
  </section>
);

const AdminSchoolPrivacySettings = ({ form, onChange }: Props) => {
  const { t } = useTranslation();
  const tp = (key: string, defaultValue: string, options: Record<string, unknown> = {}) =>
    t(`admin.school_settings.privacy.${key}`, { defaultValue, ...options });

  const annualOff = !form.annualPrivacyReviewEnabled;
  const popupOff = annualOff || !form.privacyReviewPopupEnabled;
  const emailOff = annualOff || !form.privacyReviewEmailEnabled;
  const inactiveHint = tp("inactive_hint", "Becomes active once the annual review is enabled.");
  const daysLabel = tp("days", "days");

  const toggle = (
    name: "annualPrivacyReviewEnabled" | "privacyReviewPopupEnabled" | "privacyReviewEmailEnabled",
    label: string,
    description: string,
    disabled = false
  ) => (
    <Field disabled={disabled} className={rowClass}>
      <div className="min-w-0">
        <Label className={labelClass}>{label}</Label>
        <Description className={helpClass}>{description}</Description>
      </div>
      <EmeraldSwitch checked={form[name]} onChange={(value: boolean) => onChange(name, value)} disabled={disabled} />
    </Field>
  );

  const days = (name: DayField, label: string, disabled: boolean, description?: string) => (
    <div className={rowClass}>
      <div className="min-w-0">
        <label htmlFor={name} className={labelClass}>{label}</label>
        {description && <p id={`${name}-help`} className={helpClass}>{description}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <input
          id={name}
          name={name}
          type="number"
          inputMode="numeric"
          min={1}
          max={PRIVACY_REVIEW_DAY_LIMITS[name]}
          step={1}
          required
          disabled={disabled}
          aria-describedby={description ? `${name}-help` : undefined}
          value={form[name]}
          onChange={(event) => onChange(name, event.target.value)}
          className={`${inputClass} w-28 text-right`}
        />
        <span className="text-sm text-slate-600 dark:text-slate-300">{daysLabel}</span>
      </div>
    </div>
  );

  const text = (
    name: "privacyReviewPopupTitle" | "privacyReviewPopupMessage" | "privacyReviewEmailAdditionalMessage",
    label: string,
    disabled: boolean,
    options: { rows?: number; maxLength: number; description?: string; required?: boolean }
  ) => (
    <div className="py-4">
      <label htmlFor={name} className={labelClass}>{label}</label>
      {options.description && <p id={`${name}-help`} className={helpClass}>{options.description}</p>}
      {options.rows ? (
        <textarea
          id={name}
          name={name}
          rows={options.rows}
          maxLength={options.maxLength}
          required={options.required}
          disabled={disabled}
          aria-describedby={options.description ? `${name}-help` : undefined}
          value={form[name]}
          onChange={(event) => onChange(name, event.target.value)}
          className={`${inputClass} mt-2 w-full`}
        />
      ) : (
        <input
          id={name}
          name={name}
          type="text"
          maxLength={options.maxLength}
          required={options.required}
          disabled={disabled}
          value={form[name]}
          onChange={(event) => onChange(name, event.target.value)}
          className={`${inputClass} mt-2 w-full`}
        />
      )}
    </div>
  );

  return (
    <>
      <Card title={tp("annual.title", "Annual privacy review")} delay={70}>
        {toggle(
          "annualPrivacyReviewEnabled",
          tp("annual.enabled", "Enable annual review"),
          tp("annual.enabled_description", "Ask parents periodically to review their children’s privacy settings again.")
        )}
        {days(
          "privacyReviewIntervalDays",
          tp("annual.interval", "Review interval"),
          false,
          tp("annual.interval_description", "Number of days after which parents must review and confirm the privacy settings again.")
        )}
      </Card>

      <Card title={tp("popup.title", "Popup for parents")} inactive={annualOff} hint={inactiveHint} delay={110}>
        {toggle(
          "privacyReviewPopupEnabled",
          tp("popup.enabled", "Show popup"),
          tp("popup.enabled_description", "Automatically show parents a notice when privacy settings are missing or need to be confirmed again."),
          annualOff
        )}
        {text("privacyReviewPopupTitle", tp("popup.field_title", "Title"), popupOff, { maxLength: 150, required: form.privacyReviewPopupEnabled })}
        {text("privacyReviewPopupMessage", tp("popup.field_message", "Message"), popupOff, { rows: 4, maxLength: 2000, required: form.privacyReviewPopupEnabled })}
        {days("privacyReviewSnoozeDays", tp("popup.snooze", "Show again after skipping"), popupOff)}
      </Card>

      <Card title={tp("email.title", "Email reminders")} inactive={annualOff} hint={inactiveHint} delay={150}>
        {toggle(
          "privacyReviewEmailEnabled",
          tp("email.enabled", "Send email reminders"),
          tp("email.enabled_description", "Remind parents by email when privacy settings have not been confirmed yet or need to be reviewed again."),
          annualOff
        )}
        {days(
          "privacyReviewReminderIntervalDays",
          tp("email.reminder_interval", "Reminder interval"),
          emailOff,
          tp("email.reminder_interval_description", "Number of days between reminder emails while privacy settings are missing or have not been confirmed again.")
        )}
        {text("privacyReviewEmailAdditionalMessage", tp("email.additional_message", "Additional message"), emailOff, {
          rows: 3,
          maxLength: 2000,
          description: tp("email.additional_message_description", "Optional. A short text from the school that is added to the reminder email."),
        })}
      </Card>
    </>
  );
};

export default AdminSchoolPrivacySettings;
